import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { quizQuestions, scoreQuiz } from '@/data/quiz'
import { useScentProfileStore } from '@/stores/scentProfileStore'
import { useSettings } from '@/hooks/useSettings'
import { useCatalogue } from '@/hooks/useCatalogue'
import { Button } from '@/components/ui/Button'
import { LinkButton } from '@/components/ui/LinkButton'
import { ProductCard } from '@/components/fragrance/ProductCard'
import type { ScentFamily, QuizQuestion } from '@/types'

interface ScentQuizProps {
  onComplete?: () => void
}

export function ScentQuiz({ onComplete }: ScentQuizProps) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [showResult, setShowResult] = useState(false)
  const setResult = useScentProfileStore((s) => s.setResult)
  const { quiz } = useSettings()
  const { products } = useCatalogue()
  const questions: QuizQuestion[] = quiz?.questions && quiz.questions.length > 0 ? quiz.questions : quizQuestions

  const question = questions[step]

  const selectOption = (optionId: string) => {
    const next = { ...answers, [question.id]: optionId }
    setAnswers(next)
    if (step < questions.length - 1) {
      setStep((s) => s + 1)
    } else {
      const scores = scoreQuiz(next, questions)
      const primary = (Object.keys(scores) as ScentFamily[]).reduce((a, b) =>
        scores[a] > scores[b] ? a : b
      )
      setResult({ primary, scores })
      setShowResult(true)
      onComplete?.()
    }
  }

  const reset = () => {
    setStep(0)
    setAnswers({})
    setShowResult(false)
  }

  if (showResult) {
    const result = scoreQuiz(answers, questions)
    const primary = (Object.keys(result) as ScentFamily[]).reduce((a, b) =>
      result[a] > result[b] ? a : b
    )
    const sorted = (Object.entries(result) as [ScentFamily, number][])
      .sort((a, b) => b[1] - a[1])

    // Rank the catalogue by how well each family scored, then by rating so
    // ties land on the better-loved bottle.
    const matches = [...products]
      .sort((a, b) => {
        const delta = (result[b.family] ?? 0) - (result[a.family] ?? 0)
        return delta !== 0 ? delta : b.rating - a.rating
      })
      .slice(0, 3)

    return (
      <div className="mx-auto max-w-2xl text-center">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-wd-orange">
          Your scent profile
        </p>
        <h2 className="font-display text-4xl normal-case text-carbon md:text-5xl">
          {primary}
        </h2>
        <p className="mt-4 text-carbon/60">
          Your answers lean toward {primary.toLowerCase()} fragrances — warm,
          expressive, and built to linger.
        </p>

        <div className="mt-10 space-y-4 text-left">
          {sorted.map(([family, score]) => (
            <div key={family}>
              <div className="mb-1 flex justify-between text-sm">
                <span className="text-carbon">{family}</span>
                <span className="text-carbon/60">{score} pts</span>
              </div>
              <div className="h-1.5 bg-ink/10">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(score / 18) * 100}%` }}
                  transition={{ duration: 0.8 }}
                  className="h-full bg-wd-orange"
                />
              </div>
            </div>
          ))}
        </div>

        {matches.length > 0 && (
          <div className="mt-14">
            <p className="mb-6 text-xs uppercase tracking-[0.2em] text-wd-orange">
              Three to start with
            </p>
            <div className="grid gap-6 text-left sm:grid-cols-3">
              {matches.map((perfume, index) => (
                <ProductCard key={perfume.id} perfume={perfume} index={index} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
          <Button onClick={reset}>Retake quiz</Button>
          <LinkButton to="/shop" variant="secondary">
            Shop {primary.toLowerCase()}
          </LinkButton>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-8 flex items-center justify-between text-sm text-carbon/60">
        <span>
          Question {step + 1} of {questions.length}
        </span>
        <button onClick={reset} className="hover:text-wd-orange">
          Restart
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
        >
          <h2 className="font-display text-2xl normal-case text-carbon md:text-3xl">
            {question.question}
          </h2>

          <div className="mt-8 grid gap-4">
            {question.options.map((option) => (
              <button
                key={option.id}
                onClick={() => selectOption(option.id)}
                className="border border-ink/10 bg-wd-mist px-6 py-4 text-left text-carbon transition-colors hover:border-wd-orange hover:text-wd-orange"
              >
                {option.label}
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 h-1 bg-ink/10">
        <div
          className="h-full bg-wd-orange transition-all duration-300"
          style={{ width: `${((step + 1) / questions.length) * 100}%` }}
        />
      </div>
    </div>
  )
}
